import type { ReactNode } from "react";
import { MoreVertical } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface TabHeaderProps {
  /** H2 title for the tab. */
  title: string;
  /** One-line supporting subhead. Hidden unless `showDescription` is set. */
  description?: string;
  /** Opt in to rendering the subhead (off by default). */
  showDescription?: boolean;
  /** Optional inline element rendered next to the title (e.g. an info tooltip). */
  titleAdornment?: ReactNode;
  /** Right-aligned actions (selectors, primary button, etc.). */
  children?: ReactNode;
  /** Optional kebab menu items — renders the kebab only when provided. */
  menuItems?: ReactNode;
  className?: string;
}

/**
 * Standard header used at the top of every Product Detail tab.
 * Title (H2) on the left, optional subhead, actions and kebab on the right.
 */
export function TabHeader({
  title,
  description,
  showDescription = false,
  titleAdornment,
  children,
  menuItems,
  className,
}: TabHeaderProps) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-start justify-between gap-3",
        className,
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <h2 className="text-2xl font-semibold text-foreground">{title}</h2>
          {titleAdornment}
        </div>
        {showDescription && description && (
          <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>


      {(children || menuItems) && (
        <div className="flex items-center gap-2 flex-shrink-0">
          {children}
          {menuItems && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="w-8" aria-label="More actions">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover z-50">
                {menuItems}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
    </div>
  );
}
